import { RadioGroup } from '@mui/material';
import { useState, type VoidFunctionComponent } from 'react';
import { Form, Modal } from 'react-bootstrap';

import { useDialogContext } from '../../contexts/DialogContext';
import { useRouter } from '../../contexts/RouterContext';
import { window } from '../../globals';
import {
  BUTTON_TYPE,
  FormGroup,
  LabeledCheckbox,
  LabeledRadio,
  LoaderButton,
  TextArea,
} from '../generic';

import { useGetFinishedTrial } from './useGetFinishedTrial';
import { useSubmitFinishedTrial } from './useSubmitFinishedTrialForm';

import styles from './TrialEndSurveyModal.module.less';

const SATISFACTION = [
  { label: 'Very Satisfied', value: 'very-satisfied' },
  { label: 'Satisfied', value: 'satisfied' },
  { label: 'Neutral', value: 'neutral' },
  { label: 'Unsatisfied', value: 'unsatisfied' },
  { label: 'Very Unsatisfied', value: 'very-unsatisfied' },
];

const FEATURES = [
  { label: 'License Compliance', value: 'license compliance' },
  { label: 'Open Source Vulnerability Detection', value: 'open source vulnerability detection' },
  { label: 'Dependency Management', value: 'dependency management' },
  { label: 'SBOM Generation & Management', value: 'sbom generation and management' },
  { label: 'Reporting and Analytics', value: 'reporting and analytics' },
  { label: 'Other (Please specify)', value: 'other' },
];

type TrialEndSurveyForm = {
  makeBetterNotes: string;
  satisfaction: string;
  valuableFeatures: Set<string>;
  valuableFeaturesNotes: string;
};

const DEFAULT_FORM_DATA: TrialEndSurveyForm = {
  makeBetterNotes: '',
  satisfaction: '',
  valuableFeatures: new Set(),
  valuableFeaturesNotes: '',
};

/**
 * Modal used for surveying organizations about their experience during the
 * trial.
 */
const TrialEndSurveyModal: VoidFunctionComponent = () => {
  const shouldFetchFinishedTrial = !window?.user?.super;
  const { data, isLoading } = useGetFinishedTrial(shouldFetchFinishedTrial);
  const { isLoading: isSubmitting, mutateAsync: submitForm } = useSubmitFinishedTrial();
  const [surveyData, setSurveyData] = useState<TrialEndSurveyForm>(DEFAULT_FORM_DATA);
  const { onConfirm } = useDialogContext();
  const router = useRouter();
  const [isShowing, setIsShowing] = useState<boolean>(true);

  if (isLoading) {
    return null;
  }

  if (!isLoading && (data === null || data?.submittedAt !== null)) {
    return null;
  }

  const isFeatureSelected = (option: string) => surveyData.valuableFeatures.has(option);
  const toggleFeature = (option: string) => {
    const selected = surveyData.valuableFeatures;
    if (selected.has(option)) {
      selected.delete(option);
    } else {
      selected.add(option);
    }

    setSurveyData((currentData) => ({ ...currentData, valuableFeatures: selected }));
  };

  const setOtherFeatureNotes = (newValue: string) => {
    setSurveyData((currentData) => ({ ...currentData, valuableFeaturesNotes: newValue }));
  };

  const onSubmit = async () => {
    const toSubmit = surveyData;
    if (!toSubmit.valuableFeatures.has('other')) {
      toSubmit.valuableFeaturesNotes = '';
    }

    // data will never be null or undefined at this point
    await submitForm({
      id: data!.id,
      submittedForm: {
        ...toSubmit,
        valuableFeatures: [...toSubmit.valuableFeatures], // Set does not JSON serialize
      },
    });

    setIsShowing(false);

    onConfirm({
      body: (
        <>
          We appreciate your time and effort answering these questions.
          You can always go to <strong>Subscription &gt; Plans</strong> to upgrade to our Business Plan
          and experience all of its benefits.
        </>
      ),
      confirmBtnType: BUTTON_TYPE.PRIMARY,
      confirmText: 'Upgrade to Business Plan',
      cancelText: 'Close',
      onConfirm: () => router.push('/account/settings/billing?tab=Plans'),
      title: 'Thanks for completing this survey',
    });
  };

  const canSubmit = () => {
    if (!surveyData.satisfaction) {
      return false;
    }

    if (surveyData.valuableFeatures.size === 0) {
      return false;
    }

    if (surveyData.valuableFeatures.has('other') && !surveyData.valuableFeaturesNotes) {
      return false;
    }

    if (!surveyData.makeBetterNotes) {
      return false;
    }

    return true;
  };

  return (
    <Modal show={isShowing} onHide={() => {}}>
      <Modal.Header>
        <h3>Your 14-days Business XL Trial ended</h3>
      </Modal.Header>
      <Modal.Body>
        <p className={styles.subTitle}>
          We&apos;d like to ask you some questions about your free trial experience:
        </p>

        <Form onSubmit={() => {}}>
          <FormGroup spacing="sm">
            <p className={styles.controlLabel}>
              Overall, how satisfied were you with your experience using FOSSA during the trial period?
            </p>
            <RadioGroup
              className={styles.radioItem}
              value={surveyData.satisfaction}
              onChange={(e) => setSurveyData((c) => ({ ...c, satisfaction: e.target.value }))}
            >
              { SATISFACTION.map((i) => <LabeledRadio label={i.label} value={i.value} key={`satisfaction-${i.value}`} />) }
            </RadioGroup>
          </FormGroup>

          <FormGroup spacing="sm">
            <p className={styles.controlLabel}>
              Which features of FOSSA did you find most valuable during the trial?
              <small>Select all that apply</small>
            </p>

            {
              FEATURES.map((i) => (
                <LabeledCheckbox
                  className={styles.checkBoxItem}
                  isChecked={isFeatureSelected(i.value)}
                  key={`mostValuableFeatures-${i.value}`}
                  label={i.label}
                  onChange={() => toggleFeature(i.value)}
                />
              ))
            }
            <TextArea
              isDisabled={!isFeatureSelected('other')}
              label="Type your note..."
              onChange={setOtherFeatureNotes}
              value={surveyData.valuableFeaturesNotes}
            />
          </FormGroup>

          <FormGroup spacing="sm">
            <p className={styles.controlLabel}>
              How can we make the FOSSA business tier trial experience better?
            </p>

            <TextArea
              label="Type your note..."
              onChange={(v) => setSurveyData((c) => ({ ...c, makeBetterNotes: v }))}
              value={surveyData.makeBetterNotes}
            />
          </FormGroup>

          <FormGroup>
            <LoaderButton
              onClick={onSubmit}
              isDisabled={!canSubmit()}
              isLoading={isSubmitting}
              size="medium"
              type="primary"
            >
              Submit
            </LoaderButton>
          </FormGroup>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default TrialEndSurveyModal;
